import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { HeaderSize } from '../../../types/schema';
import { getSelectedLayout } from '../../../store/selectors/collections.selectors';
import { setSelectedLayout, autoSelectMobileLayout } from '../../../store/actions/collections.actions';
import { filters as filtersGlobalAPI } from '../../../mock/globalAPI';
import { setIsFiltersPopoutOpen } from '../../../store/actions/ui.actions';
import { getNumberOfAppliedFilters, getFilterID, getColorBuckets } from '../../../store/selectors/page.selectors';
import { getLocation } from '../../../store/selectors/router.selectors';
import { findColorBucketByID } from '../Filters/utils';
import useStickElementOnScroll from '../../../hooks/useStickElementOnScroll';
import { withCssModulesClassNames } from '../../../common/nextMigrationHelpers';
import { default as styles } from '../../../scss/components/PLPToolbar.module.scss';
import { getIsXLWidth } from '../../../store/selectors/ui.selectors';

const withCssModulesClassNamesHandler = withCssModulesClassNames(styles);

interface PLPToolbarProps {
  header?: string;
  headerSize?: `${HeaderSize}`;
}

export const layoutOptions = [
  { layout: 'SingleGrid', icon: 'icon-layout-column' },
  { layout: 'DoubleGrid', icon: 'icon-layout-grid' },
];

const PLPToolbar = ({
  header,
  headerSize,
}: PLPToolbarProps) => {
  const colorBuckets = useSelector(getColorBuckets);
  const numberOfAppliedFilters = useSelector(getNumberOfAppliedFilters);
  const location = useSelector(getLocation);
  const pageFilterID = useSelector(getFilterID);
  const isDesktop = useSelector(getIsXLWidth);
  const selectedLayout = useSelector(getSelectedLayout);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const { shouldRenderPortal, portal } = useStickElementOnScroll(containerRef);

  useEffect(() => {
    dispatch(autoSelectMobileLayout());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Header = headerSize?.toLowerCase() || 'h2' as React.ElementType;
  const borderColor = location?.query?.color
    ? findColorBucketByID(location.query.color, colorBuckets)?.color
    : null;

  const renderPLPToolBar = () =>  (
    <div className={withCssModulesClassNamesHandler('plp-toolbar-nav')}>
      <Header className={withCssModulesClassNamesHandler('plp-toolbar-nav__header')}>
        {header}
      </Header>
      <div className={withCssModulesClassNamesHandler('plp-toolbar-nav__layout-filter-wrapper')}>
        <div className={withCssModulesClassNamesHandler('plp-toolbar-nav__layout-filter-wrapper__layout-buttons')}>
          {layoutOptions.map(({ layout, icon }) => (
            <button
              key={layout}
              className={withCssModulesClassNamesHandler('plp-toolbar-nav__layout-filter-wrapper__layout-option')}
              onClick={() => dispatch(setSelectedLayout(layout))}
              data-testid={selectedLayout === layout ? 'active-layout' : 'inactive-layout'}
            >
              <i className={withCssModulesClassNamesHandler(icon, { active: selectedLayout === layout })} />
            </button>
          ))}
        </div>
        {pageFilterID && !isDesktop && (
          <button
            className={withCssModulesClassNamesHandler('plp-toolbar-nav__layout-filter-wrapper__filter-button')}
            onClick={() => dispatch(setIsFiltersPopoutOpen(true))}
            data-cy="filters-popout-toggle"
            data-testid="filters-popout-toggle"
          >
            {numberOfAppliedFilters === 0
              ? <span className={withCssModulesClassNamesHandler('plp-toolbar-nav__layout-filter-wrapper__filter-icon')} />
              : (
                <span className={withCssModulesClassNamesHandler(
                  'plp-toolbar-nav__layout-filter-wrapper__filter-number',
                  {
                    'border-color-all': !borderColor,
                    [`border-color-${borderColor}`]: borderColor,
                  },
                )}
                >
                  {numberOfAppliedFilters > 9 ? '9' : numberOfAppliedFilters}
                </span>
              )}
            {filtersGlobalAPI.toggleCopy}
          </button>
        )}
      </div>
    </div>
  );
  return (
    <div className={withCssModulesClassNamesHandler('plp-toolbar')} ref={containerRef}>
      {renderPLPToolBar()}
      {shouldRenderPortal && portal && createPortal(renderPLPToolBar(), portal)}
    </div>
  );
};

PLPToolbar.propTypes = {
  header: PropTypes.string,
  headerSize: PropTypes.string,
};

PLPToolbar.defaultProps = {
  header: '',
  headerSize: '',
};

export default PLPToolbar;
